import { useEffect, useState } from "react";
import { api, apiError } from "@/lib/api";
import { useAuth } from "@/context/AuthContext";
import { resetMapsConfig } from "@/lib/maps";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Map as MapIcon, Loader2 } from "lucide-react";
import { toast } from "sonner";

export const MapsIntegrationCard = () => {
  const { hasPerm } = useAuth();
  const [status, setStatus] = useState(null);
  const [form, setForm] = useState({ browser_key: "", server_key: "", map_id: "" });
  const [busy, setBusy] = useState(false);
  const allowed = hasPerm("settings:manage");
  useEffect(() => { if (allowed) api.get("/integrations/maps").then((r) => { setStatus(r.data); setForm((f) => ({ ...f, map_id: r.data.map_id || "" })); }).catch(() => {}); }, [allowed]);
  if (!allowed) return null;

  const save = async () => {
    const body = { map_id: form.map_id };
    if (form.browser_key) body.browser_key = form.browser_key;
    if (form.server_key) body.server_key = form.server_key;
    setBusy(true);
    try { const { data } = await api.put("/integrations/maps", body); setStatus(data); setForm({ browser_key: "", server_key: "", map_id: data.map_id || "" }); resetMapsConfig(); toast.success("Maps settings saved — reload pages to apply"); }
    catch (e) { toast.error(apiError(e)); } finally { setBusy(false); }
  };
  const test = async () => {
    setBusy(true);
    try { const { data } = await api.post("/integrations/maps/test"); toast.success(`Server key works: ${data.sample.formatted}`); }
    catch (e) { toast.error(apiError(e)); } finally { setBusy(false); }
  };

  return (
    <div className="border border-border rounded-lg bg-card p-6 mt-6" data-testid="maps-integration-card">
      <div className="flex items-center gap-2 mb-1"><MapIcon className="w-4 h-4 text-brand" /><h2 className="font-heading font-semibold">Maps & Routing (Google Maps)</h2></div>
      <p className="text-sm text-muted-foreground mb-4">Add keys any time. Without them, dispatch stays manual and no drive times or ETAs are shown.</p>
      {status && (
        <div className="grid sm:grid-cols-3 gap-2 text-xs mb-4" data-testid="maps-status">
          <span>Browser key: <b>{status.browser_key || "not set"}</b></span>
          <span>Server key: <b>{status.server_key || "not set"}</b></span>
          <span>Map ID: <b>{status.map_id || "not set"}</b></span>
        </div>
      )}
      <div className="space-y-3">
        <div className="space-y-1.5"><Label>Browser key (Maps JavaScript API, restricted to your website)</Label><Input type="password" autoComplete="off" value={form.browser_key} onChange={(e) => setForm({ ...form, browser_key: e.target.value })} placeholder="Leave blank to keep current" data-testid="maps-browser-key" /></div>
        <div className="space-y-1.5"><Label>Server key (Routes API + Geocoding API)</Label><Input type="password" autoComplete="off" value={form.server_key} onChange={(e) => setForm({ ...form, server_key: e.target.value })} placeholder="Leave blank to keep current" data-testid="maps-server-key" /></div>
        <div className="space-y-1.5"><Label>Map ID (JavaScript map type)</Label><Input value={form.map_id} onChange={(e) => setForm({ ...form, map_id: e.target.value })} data-testid="maps-map-id" /></div>
        <div className="flex gap-2">
          <Button onClick={save} disabled={busy} data-testid="maps-save">{busy ? <Loader2 className="w-4 h-4 animate-spin" /> : "Save"}</Button>
          <Button variant="outline" onClick={test} disabled={busy || !status?.server_key} data-testid="maps-test">Test server key</Button>
        </div>
      </div>
    </div>
  );
};
